import { renderWinStory } from "./winStoryCard";
import api from "./api";

const W = 720, H = 1280, DURATION = 5000;
const ease = (t) => 1 - Math.pow(1 - t, 3);

/* H.264 MP4 can go straight to Instagram; anything else (VP8/VP9 WebM or VP9-in-MP4) is converted on the server. */
const pickMime = () => {
  const h264 = ["video/mp4;codecs=avc1.42E01E", "video/mp4;codecs=avc1", "video/mp4;codecs=h264"].find((m) => window.MediaRecorder?.isTypeSupported?.(m));
  if (h264) return { mime: h264, direct: true };
  const m = ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"].find((x) => window.MediaRecorder?.isTypeSupported?.(x)) || "";
  return { mime: m, direct: false };
};

const makeConfetti = (n, colors) => Array.from({ length: n }, () => ({
  x: Math.random() * W, y: -Math.random() * H, vy: 2.2 + Math.random() * 3.5, vx: (Math.random() - 0.5) * 1.6,
  s: 7 + Math.random() * 9, r: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.25, c: colors[Math.floor(Math.random() * colors.length)],
}));

/* 5-second Reels clip: zoom-in reveal + falling confetti + pulsing glow on the winning number. Returns {blob, mime}. */
export async function recordWinStoryVideo(opts, onProgress) {
  const base = await renderWinStory(opts);
  const c = document.createElement("canvas"); c.width = W; c.height = H; const ctx = c.getContext("2d");
  const colors = ["#FFC107", "#FFE082", "#10B981", "#F472B6", "#60A5FA", "#ffffff"];
  const parts = makeConfetti(140, colors);
  const { mime, direct } = pickMime();
  if (!mime) throw new Error("Video recording not supported on this browser");
  const stream = c.captureStream(30);
  const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 4_000_000 });
  const chunks = [];
  rec.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
  const done = new Promise((res) => { rec.onstop = res; });
  const draw = (t) => {
    const p = Math.min(1, t / DURATION);
    const z = 1.08 - 0.08 * ease(Math.min(1, t / 900));
    ctx.fillStyle = "#0f172a"; ctx.fillRect(0, 0, W, H);
    ctx.save(); ctx.translate(W / 2, H / 2); ctx.scale(z, z); ctx.globalAlpha = Math.min(1, t / 400);
    ctx.drawImage(base, -W / 2, -H / 2, W, H); ctx.restore(); ctx.globalAlpha = 1;
    const pulse = 0.5 + 0.5 * Math.sin(t / 180);
    ctx.save(); ctx.translate(W / 2, 1310 * (H / 1920)); ctx.strokeStyle = `rgba(255,214,102,${0.35 + 0.45 * pulse})`; ctx.lineWidth = 6 + 8 * pulse;
    ctx.shadowColor = "#FFD666"; ctx.shadowBlur = 30 + 30 * pulse; ctx.beginPath(); ctx.arc(0, 0, 130 * (W / 1080) + 14 + 10 * pulse, 0, Math.PI * 2); ctx.stroke(); ctx.restore();
    if (t > 350) {
      for (const q of parts) {
        q.y += q.vy; q.x += q.vx + Math.sin((t / 300) + q.r) * 0.6; q.r += q.vr;
        if (q.y > H + 20) { q.y = -20; q.x = Math.random() * W; }
        if (q.y < -10) continue;
        ctx.save(); ctx.translate(q.x, q.y); ctx.rotate(q.r); ctx.fillStyle = q.c; ctx.globalAlpha = 0.9; ctx.fillRect(-q.s / 2, -q.s / 4, q.s, q.s / 2); ctx.restore();
      }
    }
    if (t < 1400) {
      const a = t < 400 ? 0 : Math.sin(Math.PI * Math.min(1, (t - 400) / 1000));
      ctx.save(); ctx.globalAlpha = a * 0.9; ctx.font = "900 110px sans-serif"; ctx.textAlign = "center"; ctx.fillStyle = "#fff"; ctx.shadowColor = "#000"; ctx.shadowBlur = 24;
      ctx.translate(W / 2, H / 2); ctx.scale(1 + 0.3 * a, 1 + 0.3 * a); ctx.fillText("🎉 WINNER 🎉", 0, 0); ctx.restore();
    }
    onProgress?.(Math.round(p * 100));
  };
  rec.start(250);
  const t0 = performance.now();
  await new Promise((res) => {
    const loop = () => { const t = performance.now() - t0; draw(t); if (t < DURATION) requestAnimationFrame(loop); else res(); };
    loop();
  });
  rec.stop();
  await done;
  return { blob: new Blob(chunks, { type: mime.split(";")[0] }), mime: mime.split(";")[0], direct };
}

/* Record → (convert to H.264 MP4 on the server unless recorded as H.264 already) → share sheet / download. */
export async function shareWinStoryVideo(opts, onStage) {
  onStage?.("record", 0);
  const { blob, direct } = await recordWinStoryVideo(opts, (p) => onStage?.("record", p));
  let mp4 = blob;
  if (!direct) {
    onStage?.("convert", 100);
    const fd = new FormData(); fd.append("file", blob, "clip.webm");
    const r = await api.post("/media/to-mp4", fd, { responseType: "blob", timeout: 90000 });
    mp4 = new Blob([r.data], { type: "video/mp4" });
  }
  const file = new File([mp4], `royal-laxmi-win-${opts.number}.mp4`, { type: "video/mp4" });
  const text = `🎉 I won ₹${Math.round(opts.amount || 0)} on ROYAL LAKSHMI · ${opts.gameName} · number ${opts.number}! Play: ${opts.link}`;
  navigator.clipboard?.writeText(text).catch(() => {});
  onStage?.("share", 100);
  if (navigator.canShare?.({ files: [file] })) { await navigator.share({ files: [file], text }); return "shared"; }
  const a = document.createElement("a"); a.href = URL.createObjectURL(mp4); a.download = file.name; document.body.appendChild(a); a.click(); setTimeout(() => a.remove(), 1000);
  return "downloaded";
}
